import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

// Security validation functions
const isFakeEmail = (email: string): boolean => {
    const fakeEmailPatterns = [
        /example\.(com|org|net)/i, /test@/i, /fake@/i, /dummy@/i, /sample@/i, /@example/i, /@test\./i, /@fake\./i, /tempmail/i, /throwaway/i, /mailinator/i, /guerrillamail/i,
    ];
    return fakeEmailPatterns.some(pattern => pattern.test(email));
};

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);

    const requestReset = trpc.user.requestPasswordReset.useMutation({
        onSuccess: (data) => {
            setIsSubmitted(true);
            setIsLocalSubmitting(false);
            toast.success(data.message);
        },
        onError: (error) => {
            setIsLocalSubmitting(false);
            toast.error(error.message);
        },
    });

    const validateEmail = (val: string) => {
        if (!val) return null;
        if (isFakeEmail(val)) return "Please enter a valid, permanent email address.";
        // Basic format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(val)) return "Invalid email format.";
        return null;
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setEmail(val);
        setError(validateEmail(val));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validateEmail(email);
        if (validationError) {
            toast.error(validationError);
            setError(validationError);
            return;
        }

        if (!email) {
            toast.error('Please enter your email address');
            return;
        }

        setIsLocalSubmitting(true);
        requestReset.mutate({ email });
    };

    const isLoading = requestReset.isLoading || isLocalSubmitting;

    return (
        <div className="container mx-auto flex items-center justify-center min-h-[80vh] px-4 animate-fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "circOut" }}
                className="w-full max-w-md"
            >
                <Card className="glass shadow-2xl border-foreground/10 overflow-hidden relative">
                    {/* Ambient Background Glow */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-primary/20 blur-[60px] rounded-full pointer-events-none" />

                    <CardHeader className="space-y-1 relative z-10">
                        <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                            Forgot Password
                        </CardTitle>
                        <CardDescription className="text-center">
                            Enter your email address to recover your account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="relative z-10">
                        {isSubmitted ? (
                            <div className="text-center py-6 space-y-4 animate-fade-in">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                                >
                                    <Mail className="w-8 h-8 text-green-500" />
                                </motion.div>
                                <h3 className="text-xl font-semibold">Check your email</h3>
                                <p className="text-muted-foreground text-sm">
                                    We've sent a password reset link to <span className="font-semibold text-foreground">{email}</span>.
                                </p>
                                <Button variant="outline" className="w-full mt-4 border-foreground/10 hover:bg-foreground/5" asChild>
                                    <Link to="/login">Back to Login</Link>
                                </Button>
                                <div className="text-sm text-muted-foreground mt-4">
                                    Didn't receive the email?{' '}
                                    <button
                                        onClick={() => {
                                            setIsLocalSubmitting(true);
                                            requestReset.mutate({ email });
                                        }}
                                        className="text-primary hover:underline font-medium disabled:opacity-50"
                                        disabled={isLoading}
                                    >
                                        Click to resend
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <div className="relative group">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={handleEmailChange}
                                            disabled={isLoading}
                                            className={`pl-10 bg-background/50 border-input transition-all duration-300 focus:ring-2 focus:ring-primary/20 ${error ? 'border-red-500/50 focus:ring-red-500/20' : ''}`}
                                        />
                                    </div>
                                    {error && (
                                        <p className="text-xs text-red-500 animate-slide-up flex items-center gap-1">
                                            <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                                            {error}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-primary/25 disabled:opacity-80 disabled:cursor-not-allowed group"
                                    disabled={isLoading || !!error}
                                >
                                    <span className={`flex items-center justify-center gap-2 transition-all duration-300 ${isLoading ? 'opacity-100' : 'opacity-100'}`}>
                                        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {isLoading ? "Sending Link..." : "Send Reset Link"}
                                    </span>
                                    {!isLoading && (
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-md" />
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                    {!isSubmitted && (
                        <CardFooter className="flex justify-center border-t border-border/50 pt-4 pb-6 relative z-10">
                            <Link
                                to="/login"
                                className="text-sm text-muted-foreground hover:text-foreground flex items-center transition-colors group"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4 transform group-hover:-translate-x-1 transition-transform" />
                                Back to Login
                            </Link>
                        </CardFooter>
                    )}
                </Card>
            </motion.div>
        </div>
    );
};

export default ForgotPasswordPage;
